import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { createIngredientGroup, ingredientGroupsQuery } from '@/api'
import { languagesQuery } from '@/api/languages'
import type { IngredientGroup, IngredientGroupUpdate } from '@/api/types'
import EditIngredientGroup from '@/features/ingredients/EditIngredientGroup'

export const Route = createFileRoute('/ingredients_/displaynames_/new')({
  component: Component,
  loader: async ({ context: { queryClient } }) => {
    const languages = await queryClient.ensureQueryData(languagesQuery)

    return { languages }
  },
})

const defaultGroup: IngredientGroup = {
  name: '',
  ingredients: [],
  nameTranslations: [],
}

function Component() {
  const { languages } = Route.useLoaderData()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const saveMutation = useMutation({
    mutationFn: (group: IngredientGroupUpdate) => createIngredientGroup(group),
    onSuccess: (res) => {
      toast.success(t('savedSuccess'))

      queryClient.setQueryData<IngredientGroup[]>(ingredientGroupsQuery.queryKey, (oldData) => {
        if (oldData) {
          return [...oldData, res]
        }
      })

      navigate({
        to: '/ingredients/displaynames/$ingId',
        params: {
          ingId: res.idIngredientGroup?.toString() ?? '',
        },
      })
    },
  })

  const handleSaveGroup = (group: IngredientGroup) => {
    saveMutation.mutate({
      name: group.name,
      ingredientIds: group.ingredients.map((ig) => ig.idIngredient),
      nameTranslations: group.nameTranslations,
    })
  }

  return (
    <EditIngredientGroup
      saveGroup={handleSaveGroup}
      ingredientGroup={defaultGroup}
      languages={languages}
    />
  )
}
